type LogoKey =
	| 'logo'
	| 'logoDark'
	| 'logoLight'
	| 'logoSquare'
	| 'logoSquareDark'
	| 'logoSquareLight';

/**
 * Customer logo URLs for the active tenant.
 *
 * The registry module rewrites each declared brand asset to its served
 * `/branding/<customerId>/<file>` URL at build time, so this is a lookup, not a
 * resolution chain. A slot the customer does not declare is `null`; callers
 * fall back (the sidebar drops to the full logo when there is no square mark).
 *
 * @example
 * const { logo, logoSquare } = useCustomerLogos();
 */
export function useCustomerLogos() {
	const { branding } = useCustomerConfig();

	function assetUrl(key: LogoKey): string | null {
		return branding.value?.assets?.[key] ?? null;
	}

	const logo = computed(() => assetUrl('logo'));
	const logoDark = computed(() => assetUrl('logoDark'));
	const logoLight = computed(() => assetUrl('logoLight'));

	const logoSquare = computed(() => assetUrl('logoSquare'));
	const logoSquareDark = computed(() => assetUrl('logoSquareDark'));
	const logoSquareLight = computed(() => assetUrl('logoSquareLight'));

	/**
	 * Get the appropriate logo URL based on variant and theme.
	 *
	 * @param variant - 'full' for standard logo, 'square' for square/mark version
	 * @param theme - 'light' for dark backgrounds, 'dark' for light backgrounds, 'default' for neutral
	 */
	function getThemedLogo(
		variant: 'full' | 'square' = 'full',
		theme: 'light' | 'dark' | 'default' = 'default',
	): string | null {
		if (variant === 'square') {
			if (theme === 'light') return logoSquareLight.value;
			if (theme === 'dark') return logoSquareDark.value;
			return logoSquare.value;
		}

		if (theme === 'light') return logoLight.value;
		if (theme === 'dark') return logoDark.value;
		return logo.value;
	}

	return {
		logo: readonly(logo),
		logoDark: readonly(logoDark),
		logoLight: readonly(logoLight),

		logoSquare: readonly(logoSquare),
		logoSquareDark: readonly(logoSquareDark),
		logoSquareLight: readonly(logoSquareLight),

		getThemedLogo,
	};
}
